import React, { useMemo, useCallback, useContext, useState } from 'react';
import { LocalizationContext } from 'app/localization/Context';
import { getBindTemplate, BindTemplateType, createBindGenericConditionInfoObject, createBindGenericActionInfoObject, BindSectionType, createBindComparisonConditionInfoObject, BindSliderType } from 'app/model/BindData';
import { BindConditionType, BindActionType } from 'app/model/Bind';

import { BrightnessSlider, HumiditySlider, TemperatureSlider } from 'app/home/general-components/Slider/AdvanceSliders';

import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconBack } from 'images/general/back.svg';
import { ReactComponent as IconBackBlack } from 'images/general/back-black.svg';

import './SelectAction.scss'


export default function SelectAction({
    pageData, back, dismissHandler,
    preBind, pushState,
}) {

    const l = useContext(LocalizationContext);
    const s = l.strings;
    const b = s.createBindProcess;

    const device = pageData.device;

    const template = useMemo(() => {
        const t = getBindTemplate(pageData.stage === "if", device, l);
        if (t.type !== BindTemplateType.optionList) {
            console.log("Bind template not supported", t, "for device", device, "forCondition?", pageData.stage === "if");
            return {};
        } else {
            return t;
        }
    }, [pageData.stage, device, l]);

    const _initailSelectedKeyValues = useMemo(() => {
        let infos = [];
        if (pageData.stage === "if") {
            infos = preBind.conditionInfos.filter(info =>
                (info.data.condition_type === BindConditionType.device) &&
                (info.data.device_mac === device.mac));
        } else {
            infos = preBind.actionInfos.filter(info =>
                (info.data.action_type === BindActionType.device) &&
                (info.data.device_mac === device.mac));
        }

        const returnValue = {};
        for (let i = 0; i < infos.length; i++) {
            const data = infos[i].data;
            const notIntrestedKeys = ["device_mac", "condition_type", "action_type"]; // already filtered out
            for (const key in data) {
                if (Object.prototype.hasOwnProperty.call(data, key) && !notIntrestedKeys.includes(key)) {
                    returnValue[key] = data[key]; // can be a comparison object
                }
            }
        }
        return returnValue;
    }, [preBind, device.mac, pageData.stage]);

    const [selectedKeyValues, setSelectedKeyValues] = useState(_initailSelectedKeyValues);

    const annotatedTemplate = useMemo(() => {
        // will add a 'selected' property to sections *and* items.
        const newTemplate = { ...template };
        newTemplate.sections = [];
        for (let i = 0; i < template.sections.length; i++) {
            const newSection = { ...template.sections[i] };
            newSection.selected = false;
            if (newSection.type === BindSectionType.oneOf) {
                newSection.items = [];
                for (let j = 0; j < template.sections[i].items.length; j++) {
                    const newItem = { ...template.sections[i].items[j] };
                    newItem.selected = selectedKeyValues[newItem.key] === newItem.value;
                    if (newItem.selected) {
                        newSection.selected = true;
                    }
                    newSection.items.push(newItem)
                }
            } else if (newSection.type === BindSectionType.comparisonWithSlider) {
                if (selectedKeyValues[newSection.key] !== undefined) {
                    newSection.selected = true;
                    newSection.selectedComparisonCode = selectedKeyValues[newSection.key].comparison;
                }
            }
            newTemplate.sections.push(newSection);
        }
        return newTemplate;
    }, [selectedKeyValues, template]);

    const onCheckboxClick = useCallback((section) => {
        // remove all for this section
        let newSelectedKeyValues = { ...selectedKeyValues }
        if (section.type === BindSectionType.oneOf) {
            for (let j = 0; j < section.items.length; j++) {
                let item = section.items[j];
                if (newSelectedKeyValues[item.key] === item.value) {
                    delete newSelectedKeyValues[item.key]
                }
            }
        } else if (section.type === BindSectionType.comparisonWithSlider) {
            if (selectedKeyValues[section.key] !== undefined) {
                delete newSelectedKeyValues[section.key]
            }
        }
        setSelectedKeyValues(newSelectedKeyValues);
    }, [selectedKeyValues]);

    const onItemClick = useCallback((item, section) => {
        let newSelectedKeyValues = { ...selectedKeyValues }
        if (newSelectedKeyValues[item.key] === item.value) {
            delete newSelectedKeyValues[item.key]
        } else {
            newSelectedKeyValues[item.key] = item.value
        }
        setSelectedKeyValues(newSelectedKeyValues);
    }, [selectedKeyValues]);

    const onComparisonObjectChange = useCallback((changedElements, section) => {
        // changedElements is either {value: ...} or {comparison: ...}
        let newSelectedKeyValues = { ...selectedKeyValues }
        if (newSelectedKeyValues[section.key] === undefined) { // already exits
            let comparison = 0;
            let value = section.defaulSliderValue;
            if (changedElements.comparison !== undefined)
                comparison = changedElements.comparison;
            if (changedElements.value !== undefined)
                value = changedElements.value;
            newSelectedKeyValues[section.key] = { comparison, value }
        } else {
            newSelectedKeyValues[section.key] = { ...newSelectedKeyValues[section.key] };
            if (changedElements.comparison !== undefined) {
                if (changedElements.comparison === newSelectedKeyValues[section.key].comparison) {
                    delete newSelectedKeyValues[section.key]
                } else {
                    newSelectedKeyValues[section.key].comparison = changedElements.comparison
                }
            } else if (changedElements.value !== undefined) {
                newSelectedKeyValues[section.key].value = changedElements.value
            }
        }
        setSelectedKeyValues(newSelectedKeyValues);
    }, [selectedKeyValues]);

    const onNextClick = useCallback(() => {
        const _preBind = { ...preBind };
        if (pageData.stage === "if") {
            let _conditionInfos = [..._preBind.conditionInfos];
            _conditionInfos = _conditionInfos.filter(info =>
                (info.data.condition_type !== BindConditionType.device) ||
                (info.data.device_mac !== device.mac)
            );
            for (let i = 0; i < annotatedTemplate.sections.length; i++) {
                const section = annotatedTemplate.sections[i];
                if (section.type === BindSectionType.oneOf) {
                    for (let j = 0; j < section.items.length; j++) {
                        const item = section.items[j];
                        if (item.selected) {
                            _conditionInfos.push(
                                createBindGenericConditionInfoObject(
                                    section, item, device.name, device.mac, device.type));
                        }
                    }
                } else if (section.type === BindSectionType.comparisonWithSlider) {
                    if (section.selected) {
                        _conditionInfos.push(
                            createBindComparisonConditionInfoObject(
                                section, selectedKeyValues[section.key], device.name, device.mac, device.type, l));
                    }
                }
            }
            _preBind.conditionInfos = _conditionInfos;
        } else {
            let _actionInfos = [..._preBind.actionInfos];
            _actionInfos = _actionInfos.filter(info =>
                (info.data.action_type !== BindActionType.device) ||
                (info.data.device_mac !== device.mac)
            );
            for (let i = 0; i < annotatedTemplate.sections.length; i++) {
                const section = annotatedTemplate.sections[i];
                if (section.type === BindSectionType.oneOf) {
                    for (let j = 0; j < section.items.length; j++) {
                        const item = section.items[j];
                        if (item.selected) {
                            _actionInfos.push(
                                createBindGenericActionInfoObject(
                                    section, item, device.name, device.mac, device.type));
                        }
                    }
                }
            }
            _preBind.actionInfos = _actionInfos;
        }
        pushState({ preBind: _preBind, pageData: { name: 'main' } })
    }, [pageData.stage, preBind, l, selectedKeyValues, pushState,
        annotatedTemplate, device.name, device.mac, device.type]);

    return (
        <div className="create-bind-page-content">
            <IconBack className="nav-icon" /> <div className="nav-button" onClick={() => { back(); }} />
            <IconX className="nav-icon-secondary" /> <div className="nav-button-secondary" onClick={dismissHandler} />

            <div className="header">{(pageData.stage === 'if') ? b.if : b.then} {device.name}</div>

            {annotatedTemplate.sections.map((section, index) => (
                <div className="section" key={"bind-section-" + String(index)}>
                    {section.selected ? (
                        <div className="check-box" onClick={() => { onCheckboxClick(section) }} style={{ borderColor: section.color }}>
                            <div className="dot" style={{ backgroundColor: section.color }} />
                        </div>
                    ) : (
                        <div className="check-box">
                        </div>
                    )}
                    <div className="name">{section.name}</div>
                    {section.type === BindSectionType.oneOf ? (
                        <div className="items">
                            {section.items.map((item, index2) => (
                                item.selected ?
                                    (
                                        <div className="item"
                                            key={"bind-section-" + String(index) + "-item-" + String(index2)}
                                            style={{ backgroundColor: section.color }}
                                            onClick={() => onItemClick(item, section)} >

                                            {React.createElement(item.iconDark, { className: "icon" })}
                                            <div className="title" style={{ color: "#111111" }}>
                                                {item.title}
                                            </div>
                                        </div>
                                    ) :
                                    (
                                        <div className="item"
                                            key={"bind-section-" + String(index) + "-item-" + String(index2)}
                                            onClick={() => onItemClick(item, section)} >

                                            {React.createElement(item.icon, { className: "icon" })}
                                            <div className="title" style={{ color: section.color }}>
                                                {item.title}
                                            </div>
                                        </div>
                                    )
                            ))}
                        </div>
                    ) : (section.type === BindSectionType.comparisonWithSlider ? (
                        <div className="comparison-with-slider">
                            <div className="comparison">
                                <div className="item"
                                    style={{
                                        backgroundColor: section.selectedComparisonCode === 0 ? section.color : undefined,
                                        color: section.selectedComparisonCode === 0 ? "#111111" : section.color
                                    }}
                                    onClick={() => {
                                        onComparisonObjectChange({ comparison: 0 }, section)
                                    }}>
                                    <div className="symbol">&#60;</div>
                                    <div className="text">{b.whenConditionComparison.lower}</div>
                                </div>
                                <div className="item"
                                    style={{
                                        backgroundColor: section.selectedComparisonCode === 1 ? section.color : undefined,
                                        color: section.selectedComparisonCode === 1 ? "#111111" : section.color
                                    }}
                                    onClick={() => {
                                        onComparisonObjectChange({ comparison: 1 }, section)
                                    }}>
                                    <div className="symbol">=</div>
                                    <div className="text">{b.whenConditionComparison.equal}</div>
                                </div>
                                <div className="item"
                                    style={{
                                        backgroundColor: section.selectedComparisonCode === 2 ? section.color : undefined,
                                        color: section.selectedComparisonCode === 2 ? "#111111" : section.color
                                    }}
                                    onClick={() => {
                                        onComparisonObjectChange({ comparison: 2 }, section)
                                    }}>
                                    <div className="symbol">&#62;</div>
                                    <div className="text">{b.whenConditionComparison.higher}</div>
                                </div>
                            </div>

                            <div className="slider-container" dir="ltr">
                                {section.sliderType === BindSliderType.temperature ?
                                    <TemperatureSlider min={-10} max={70} initalValue={section.defaulSliderValue} step={1} onChange={(value) => { onComparisonObjectChange({ value }, section); }} />
                                    : (section.sliderType === BindSliderType.humidity ?
                                        <HumiditySlider min={0} max={100} initalValue={section.defaulSliderValue} step={1} onChange={(value) => { onComparisonObjectChange({ value }, section); }} />
                                        : (section.sliderType === BindSliderType.brightness ?
                                            <BrightnessSlider min={0} max={6} initalValue={section.defaulSliderValue} step={1} lightLevels={s.lightLevels} onChange={(value) => { onComparisonObjectChange({ value }, section); }} />
                                            : null))}
                            </div>
                        </div>
                    ) : null)}

                </div>
            ))}


            <div style={{ width: 10, height: 64 + 8 }} />

            <div className="full-blue-button" onClick={onNextClick}>
                <div>{b.next}</div> <IconBackBlack className="icon-180" />
            </div>
        </div>
    );
}

// function KeyValuePair(key, value) {
//     return String(key) + ";" + String(value);
// }